import { ref, computed } from 'vue'
import type { Article } from '~/models/content'
import {
  makeTwitterSharableLink,
  makeLinkedinSharableLink,
} from '@/utils/string'
import type { TGeneral } from '~/types/app.type'

export function useArticle(slug: string) {
  const article = ref<Article | null>(null)
  const loading = ref(true)
  const error = ref(null)
  const twitterUrl = ref<string>('')
  const linkedinUrl = ref<string>('')

  const { $graphqlClient, $rudderstack }: TGeneral = useNuxtApp()

  const fetchArticle = async () => {
    loading.value = true
    try {
      const fetchedArticle = await $graphqlClient.getArticleDetail({ slug })
      if (!fetchedArticle) {
        error.value = { statusCode: 404 }
        return
      }

      article.value = fetchedArticle
      const url = window.location.href
      const { title = '' } = article.value

      twitterUrl.value = makeTwitterSharableLink(url, title)
      linkedinUrl.value = makeLinkedinSharableLink(url, title)

      const { viewerCanRead = false } = article.value
      if (!viewerCanRead) {
        $rudderstack.trackContentGatedInteraction()
      }
    } catch (err) {
      error.value = err
    } finally {
      loading.value = false
    }
  }

  const getAuthorTitle = () => {
    const { author = { fullName: '', jobTitle: '' } } = article.value
    const values = [author.fullName, author.jobTitle].filter((value) => value)

    return `By ${values.join(', ')}`
  }

  const contentHtml = computed(() => {
    const { viewerCanRead = false, content = '', excerpt = '' } = article.value
    return viewerCanRead ? content : excerpt
  })

  const optimizedCover = computed(() => {
    const { $cloudinary } = useNuxtApp()
    const { coverUrl = '' } = article.value
    return $cloudinary.resize(coverUrl, 'article-detail-cover')
  })

  return {
    article,
    loading,
    error,
    twitterUrl,
    linkedinUrl,
    fetchArticle,
    getAuthorTitle,
    contentHtml,
    optimizedCover,
  }
}
